import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import type { AnalyticsEnvelope, AnalyticsSection, Metric } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { ChartSkeleton } from '#client/components/charts';
import { EmptyState } from '#client/components/ui/EmptyState';
import { useDelayedFlag } from '#client/hooks/useDelayedFlag';
import { AnalyticsFilterBar } from './AnalyticsFilterBar';
import { MetricRow } from './MetricTile';
import type { AnalyticsFilterState } from './useAnalyticsFilters';
import { useAnalyticsFilterOptions } from './useAnalyticsSection';

interface SectionShellProps<T extends AnalyticsEnvelope> {
  section: AnalyticsSection;
  filters: AnalyticsFilterState;
  data: T | undefined;
  loading: boolean;
  error?: string | undefined;
  /** Headline tiles, taken from the payload. */
  metrics?: readonly Metric[] | undefined;
  /** True when the range genuinely produced nothing. */
  empty?: boolean | undefined;
  emptyMessage?: string | undefined;
  children: ReactNode;
}

/**
 * Filter bar, tiles, caveats, then the section's own content.
 *
 * The order matters and is the same on every section: what you are looking at,
 * the headline, what to distrust about it, then the detail. A caveat strip
 * below the charts is a caveat nobody reads.
 */
export function SectionShell<T extends AnalyticsEnvelope>({
  section,
  filters,
  data,
  loading,
  error,
  metrics,
  empty,
  emptyMessage,
  children,
}: SectionShellProps<T>) {
  const options = useAnalyticsFilterOptions();
  // 250ms, the house threshold: below about 200ms a skeleton reads as a flicker
  // and costs more attention than the wait it was hiding.
  const showSkeleton = useDelayedFlag(loading && data === undefined, 250);

  return (
    <div className="c-an">
      <AnalyticsFilterBar section={section} filters={filters} options={options.data} />

      {error && <Banner tone="critical">{error}</Banner>}

      {showSkeleton && <SectionSkeleton />}

      {data && (
        <>
          {metrics && metrics.length > 0 && <MetricRow metrics={metrics} />}

          {data.notes.length > 0 && (
            <ul className="c-an-notes">
              {data.notes.map((note) => (
                <li key={note} className="c-an-notes__item">
                  <Info size={14} aria-hidden="true" />
                  {note}
                </li>
              ))}
            </ul>
          )}

          {empty ? (
            <EmptyState
              title="Nothing in this period"
              body={emptyMessage ?? 'Try a longer range, or clear the filters.'}
            />
          ) : (
            children
          )}
        </>
      )}
    </div>
  );
}

/**
 * A placeholder with the section's geometry.
 *
 * Mirrors the real layout — a tile row and two charts — so the page does not
 * reflow when the data lands. A chart that appears and shoves the table below it
 * down the page costs more attention than the wait did.
 */
function SectionSkeleton() {
  return (
    <div className="c-an-skeleton" aria-hidden="true">
      <div className="c-an-tiles">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="c-an-tile c-an-tile--skeleton" />
        ))}
      </div>
      <div className="c-an__grid">
        <div className="c-an__cell c-an__cell--wide">
          <ChartSkeleton shape="line" />
        </div>
        <div className="c-an__cell">
          <ChartSkeleton shape="bars" />
        </div>
      </div>
    </div>
  );
}
